(ns common.util
  (:require ["moment-timezone" :as moment-tz]
            ["moment/min/moment-with-locales$default" :as moment]
            ["react" :as react]
            [camel-snake-kebab.core :refer [->kebab-case]]
            [camel-snake-kebab.extras :refer [transform-keys]]
            [clojure.string :as str]
            [clojure.walk :as walk]
            [goog.crypt.base64 :as base64]
            [goog.i18n.NumberFormat]
            [goog.i18n.NumberFormat.Format]
            [goog.string :refer [format toNumber]]
            [goog.string.format]
            [mate.core :as mc])
  (:require-macros [common.util]))

(defn dissoc-many [m keys]
  (apply dissoc m keys))

(defn distinct-by
  "Same as distinct except that the test is made using a given function f.
   Only the first value x for a seen value (f x) is kept in the result."
  [f coll]
  (let [step (fn step [xs seen]
               (lazy-seq
                ((fn [[x :as xs] seen]
                   (when-let [s (seq xs)]
                     (let [k (f x)]
                       (if (contains? seen k)
                         (recur (rest s) seen)
                         (cons x (step (rest s) (conj seen k)))))))
                 xs seen)))]
    (step coll #{})))

(defn m->kebab-case
  "Convert map into kebab cased keys"
  [m]
  (->> (js->clj m :keywordize-keys true)
       (transform-keys ->kebab-case)))

(defn ^{:source "https://github.com/day8/re-frame/blob/fbf76c92/src/re_frame/subs.cljc#L112"}
  map-vals
  "Returns a new version of 'm' in which 'f' has been applied to each value.
  (map-vals inc {:a 4, :b 2}) => {:a 5, :b 3}"
  [f m]
  (into (empty m)
        (map (fn [[k v]] [k (f v)]))
        m))

(defn maps->map
  "Reduces a collection of maps into a map
   where `fk` transforms a key and `fv` transforms the value

   Example:
   ```clojure
   (maps->map
     '({:name \"foo\" :values [\"test\"]})
     :name
     :values) ;;=> {\"foo\" [\"test\"]}
   ```"
  [maps fk fv]
  (reduce #(assoc %1 (fk %2) (fv %2))
          {} maps))

(defn keywordize-strings
  [xs]
  (mapv (fn [x]
          (if (string? x)
            (keyword x)
            x))
        xs))

(defn left-join
  "Left join `xrel` and `yrel`.
  Joins on value for `key-x` in `xrel` and value for `key-y` in `yrel`"
  ([xrel yrel key-x] (left-join xrel yrel key-x key-x))
  ([xrel yrel key-x key-y]
   (let [indexed (zipmap (map key-y yrel) yrel)]
     (map #(merge % (get indexed (key-x %))) xrel))))

(defn vec-dissoc
  "dissoc item at `idx` in `coll`"
  [coll idx]
  (vec (concat (subvec coll 0 idx) (subvec coll (inc idx)))))

(defn vec-move
  "Move item at `old-idx` to `new-idx`, shifting subsequent items to the right."
  [coll old-idx new-idx]
  (let [item (get coll old-idx)
        [left right] (split-at new-idx (vec-dissoc coll old-idx))]
    (into [] (concat left [item] right))))

(defn vec-insert
  "Insert `item`` at `idx`, shifting subsequent items to the right."
  [coll item idx]
  (let [[left right] (split-at idx coll)]
    (into [] (concat left [item] right))))

(defn filter-nils-map
  "Remove map entries with `nil` values in map `m` and any nested maps. When
  :remove-empty-maps? option is true, empty maps are removed.
  :remove-blank-strings? option is true, empty strings are removed."
  ([m] (filter-nils-map m :remove-empty-maps? false :remove-blank-strings? false))
  ([m & {:keys [remove-empty-maps? remove-blank-strings?]}]
   (letfn [(should-remove? [v]
             (or (nil? v)
                 (and remove-blank-strings? (= "" v))))
           (walk [v]
             (if (map? v)
               (cond-> (into {} (remove (comp should-remove? second) v))
                 remove-empty-maps? not-empty)
               v))]
     (or (walk/postwalk walk m) {}))))

(defn remove-duplicate-by
  "Returns coll where duplicates are deduped.
   Duplicates are defined by eval f on each element."
  [f coll]
  (map first (vals (group-by f coll))))

(defn leaf-paths
  "Examples:
  (leaf-paths {:a {:b 2} :c [1 2]})
  ;; => [[:a :b] [:c]]

  (leaf-paths {}) ;; => []
  (leaf-paths []) ;; => []
  "
  {:author "Alex Miller"}
  [m]
  (if (map? m)
    (vec
     (mapcat (fn [[k v]]
               (let [sub (leaf-paths v)
                     nested (map #(into [k] %) (filter (comp not empty?) sub))]
                 (if (seq nested)
                   nested
                   [[k]])))
             m))
    []))

(defn pull
  "Examples:
  (pull {:a {:b 2 :c 3}} [[:a]])
  ;; => {:a {:b 2 :c 3}}

  (pull {:a {:b 2 :c 3}} [[:a :c]])
  ;; => {:a {:c 3}}

  ;; non existing keys are ignored
  (pull {:foo 1} [[:bar]])
  ;; => {}

  ;; keys traversing into vectors are ignored
  (pull {:a [:first]} [[:a 0]])
  ;; => {}
  ;; int-keys in maps work though
  (pull {:a {0 :first}} [[:a 0]])
  ;; => {:a {0 :first}}

  options:
  - remove-vals - exclude vals for which (remove-vals v) returns logical true.

  "
  ([m ks]
   (pull m ks {}))
  ([m ks {:keys [remove-vals] :or {remove-vals (constantly false)}}]
   {:pre [(or (nil? m) (map? m))]}
   (let [not-vector-traversing (fn [k]
                                 (let [int-positions (map first (filter (comp int? second)
                                                                        (mc/seq-indexed k)))]
                                   (not-any? #(vector? (get-in m (take % k)))
                                             int-positions)))
         valid-key? (every-pred seq not-vector-traversing)]
     (reduce (fn [acc k]
               (let [v (get-in m k ::not-found)]
                 (if (and (valid-key? k)
                          (not= v ::not-found)
                          (not (remove-vals v)))
                   (assoc-in acc k v)
                   acc)))
             {} ks))))

;; original https://github.com/metosin/potpuri
(defn path-vals
  "Returns vector of tuples containing path vector to the value and the value."
  [m]
  (letfn
   [(pvals [l p m]
      (reduce
       (fn [l [k v]]
         (if (map? v)
           (pvals l (conj p k) v)
           (cons [(conj p k) v] l)))
       l m))]
    (pvals [] [] m)))

;; original https://github.com/metosin/potpuri
(defn assoc-in-path-vals
  "Re-created a map from it's path-vals extracted with (path-vals)."
  [c]
  (reduce (partial apply assoc-in) {} c))

(defn wrap-with-quotes
  [string]
  (str "\"" string "\""))

(def conjv (comp vec conj))

(defn ->locale-string
  ([integer] (->locale-string integer {}))
  ([integer options] (->locale-string integer js/navigator.language options))
  ([integer locale options]
   (when integer
     (.toLocaleString integer locale (clj->js options)))))

(defn ->month-name [month]
  (-> (moment (str month) "M")
      (.format "MMMM")))

(defn to-number [v]
  (toNumber v))

(def format-string
  format)

(defn format-timestamp
  ([timestamp] (format-timestamp timestamp "L LT"))
  ([timestamp date-format]
   (-> (moment (* 1000 timestamp))
       (.format date-format))))

(defn format-iso-string
  ([iso-string] (format-iso-string iso-string "L LT"))
  ([iso-string date-format]
   (-> (moment iso-string)
       (.format date-format))))

(def timezones
  (js->clj (.. moment-tz -tz names)))

(def user-timezone
  (.. moment-tz -tz guess))

(defn now []
  (let [date (new js/Date)]
    (new js/Date
         (.getUTCFullYear date)
         (.getUTCMonth date)
         (.getUTCDate date)
         (.getUTCHours date)
         (.getUTCMinutes date)
         (.getUTCSeconds date))))

(defn human-readable-relative-time
  ([timestamp] (human-readable-relative-time timestamp (moment)))
  ([timestamp-earlier timestamp-later]
   (.from (moment timestamp-earlier) (moment timestamp-later))))

(defn today []
  (let [date (now)]
    (.setUTCHours date 0 0 0 0)
    date))

(defn yesterday []
  (let [date (today)]
    (.setUTCDate date (- (.getUTCDate date) 1))
    date))

(defn tomorrow []
  (let [date (today)]
    (.setUTCDate date (+ (.getUTCDate date) 1))
    date))

(defn n-days-ago [n]
  (let [date (today)]
    (.setUTCDate date (- (.getUTCDate date) n))
    date))

(defn start-of-this-month []
  (let [date (today)]
    (.setUTCDate date 1)
    date))

(defn start-of-last-month []
  (let [date (start-of-this-month)]
    (.setUTCMonth date (dec (.getUTCMonth date)))
    date))

(defn start-of-next-month []
  (let [date (start-of-this-month)]
    (.setUTCMonth date (inc (.getUTCMonth date)))
    date))

(def not-blank? (complement str/blank?))

(defn remove-newlines
  [v]
  (if (string? v)
    (str/replace v #"\n" "\\n")
    v))

(def string-number? (comp not js/isNaN))

;; moment requires date formats to be in a JS array
(defn valid-date-string?
  ([s] (valid-date-string?
        s (clj->js [(.-ISO_8601 moment)
                    "YYYY-MM-DD"
                    "YYYY/MM/DD"
                    "MM-DD-YYYY"
                    "MM/DD/YYYY"])
        {:strict? true}))
  ([s formats] (valid-date-string? s formats {:strict? true}))
  ([s formats {:keys [strict?] :or {strict? true}}]
   (.isValid (moment s formats strict?))))

;; https://www.elastic.co/guide/en/elasticsearch/reference/current/common-options.html#date-math
;; supporting all options except seconds
(def elasticsearch-datestring-regex
  #"^now([-+]\d+([mhHdDwMy]))*([/]\2)*$")

(defn valid-elasticsearch-date-string?
  [date-string]
  (and (string? date-string)
       (some? (re-matches elasticsearch-datestring-regex date-string))))

;; copied from https://github.com/weavejester/medley/blob/master/src/medley/core.cljc#L38
(defn assoc-some
  "Associates a key with a value in a map, if and only if the value is not nil."
  ([m k v]
   (if (nil? v) m (assoc m k v)))
  ([m k v & kvs]
   (reduce (fn [m [k v]] (assoc-some m k v))
           (assoc-some m k v)
           (partition 2 kvs))))

;; copied from https://github.com/weavejester/medley/blob/master/src/medley/core.cljc#L291-L299
(defn index-by
  "Returns a map of the elements of coll keyed by the result of f on each
  element. The value at each key will be the last element in coll associated
  with that key. This function is similar to `clojure.core/group-by`, except
  that elements with the same key are overwritten, rather than added to a
  vector of values."
  {:added "1.2.0"}
  [f coll]
  (persistent! (reduce #(assoc! %1 (f %2) %2) (transient {}) coll)))

(defn conj-some
  "Conjs a value with a sequence, if and only if the value is not nil."
  ([s v]
   (if (nil? v) s (conj s v)))
  ([s v & vs]
   (reduce #(conj-some %1 %2)
           (conj-some s v)
           vs)))

(defn submap?
  "Is m1 a subset of m2?"
  [m1 m2]
  (if (and (map? m1) (map? m2))
    (every? (fn [[k v]] (and (contains? m2 k)
                             (submap? v (get m2 k))))
            m1)
    (= m1 m2)))

(defn percentage
  "Format n to a percentage with a minimum/maximum number of decimals
   e.g. 0.25 :=> 25%
        0.254 :=> 25.4%
        0.2555 :=> 25.5%"
  [n & {:keys [min max]
        :or {min 0
             max 1}}]
  (let [formatter (goog.i18n.NumberFormat. (.-PERCENT goog.i18n.NumberFormat.Format))]
    (.setMinimumFractionDigits formatter min)
    (.setMaximumFractionDigits formatter max)
    (.format formatter n)))

(defn moment->timemap
  "Converts a Moment into a timemap."
  [^js moment]
  (when (.isValid moment)
    {:hour (.hours moment)
     :minute (.minutes moment)}))

(defn timemap->moment
  "Converts a timemap into a Moment."
  [{:keys [minute hour]}]
  (when (and (nat-int? minute) (nat-int? hour))
    (let [m (moment)]
      (.hours m hour)
      (.minutes m minute))))

(defn str->int
  "Converts a string to an integer"
  [str]
  (when-not (str/blank? str)
    (js/parseInt str)))

(defn preview-token->auth-token
  [preview-token]
  (some-> preview-token
          base64/decodeString
          js/JSON.parse
          (js->clj :keywordize-keys true)
          :d
          js/JSON.parse
          (js->clj :keywordize-keys true)
          :auth
          (->> (str "lemonpi "))))

(defn parse-args
  "Given React style arguments, return [props-map children] tuple."
  [args]
  (if (map? (first args))
    [(first args) (rest args)]
    [nil args]))

(defn keyword-name [x]
  (cond
    (qualified-keyword? x) (str (namespace x) "/" (name x))
    (simple-keyword? x) (name x)
    :else x))

(defn merge-objects
  "Merges JavaScript Objects"
  [& objects]
  (apply js/Object.assign #js {} objects))

(defn merge-refs
  "Similar to react-merge-refs.
  Takes list of React refs as args list,
  returns a function to be used as ref value,
  calling each ref fn or setting the current property of the ref value.

  NOTE: The returned fn is not stable, it will change on every render."
  [& refs]
  (fn [v]
    (doseq [r refs]
      (if (fn? r)
        (r v)
        (when (some? r)
          (set! (.-current r) v))))))

(defn use-merge-refs
  "Same as merge-refs but the returned fn is stable.

  Can be important because changing a ref fn value triggers
  ref unmount & mount calls."
  [& refs]
  (react/useCallback (apply merge-refs refs)
                     (into-array refs)))

(defn sanitize-fieldname
  "Replacing whitespaces with underscore and removing any special symbols afterwards."
  [fieldname]
  (-> fieldname
      (str/trim)
      (str/replace #" " "_")
      (str/replace #"[^a-zA-Z0-9\-_]" "")))

(defn vec-swap
  "Swaps the first item in `v` with `item` for which `pred` returns true.
   Returns `v` when `pred` don't return any true value"
  [v pred new-item]
  (if-some [index (first (keep-indexed
                          (fn [index item]
                            (when (pred item)
                              index))
                          v))]
    (assoc v index new-item)
    v))
